/**
 * A filing date (balansdatum, vaststellingsdatum) is a calendar date, not an instant: '2025-12-31' and
 * '2026-04-20T00:00:00Z' both mean that day, wherever the reader sits.
 *
 * Handing such a value to 'new Date()' turns it into UTC midnight, which the local getters then read back as
 * the day before for anyone west of UTC - and writing it out again would save that shifted day. These helpers
 * read the year-month-day off the text instead, so a calendar date stays the day it says.
 */

/** The year the API sends for a date that was never set, its date fields not being nullable */
const UnsetDateYear: number = 1;

/**
 * Reads a calendar date without letting the timezone move it.
 * @param value a 'yyyy-MM-dd' or full ISO string, or a Date
 * @returns a local Date on that same calendar day, or null when there is no usable date
 */
export function ParseCalendarDate(value: Date | string | null | undefined): Date | null {
    if (!value) {
        return null;
    }
    if (value instanceof Date) {
        return isNaN(value.getTime()) ? null : value;
    }

    const yearMonthDay = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim());
    if (yearMonthDay) {
        const year = +yearMonthDay[1];
        // built from the parts, so the day cannot shift whatever the reader's offset is
        const date = new Date(year, +yearMonthDay[2] - 1, +yearMonthDay[3]);
        // the constructor maps years 0-99 into the 1900s, which would turn the API's unset 0001 into 1901
        date.setFullYear(year);
        return date;
    }

    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * Whether there is no real date here: missing, unparseable, or the year 1 the API sends for a date that was
 * never set.
 */
export function IsUnsetCalendarDate(value: Date | string | null | undefined): boolean {
    const date = ParseCalendarDate(value);
    return date === null || date.getFullYear() <= UnsetDateYear;
}

/**
 * Formats a date as the 'yyyy-MM-dd' the filing API reads dates in, using the date's own calendar day.
 */
export function ToCalendarDateString(date: Date): string {
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
}
